import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "../styles/SelectBonusRaces.css";

import vallon from "../../medias/png/icons/vallon.png";
import chrono from "../../medias/png/icons/chrono.png";
import plat from "../../medias/png/icons/plat.png";
import montagne from "../../medias/png/icons/montagne.png";

const SelectBonusRace = ({
  selectedRaces,
  setBonusRaces,
  bonusRaces,
  races,
  prevStep,
  saveData,
}) => {
  const { t } = useTranslation();
  const icons = {
    Vallon: vallon,
    Chrono: chrono,
    Plat: plat,
    Montagne: montagne,
  };

  const [confirmValidDataPopup, setConfirmValidDataPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [divisionFilter, setDivisionFilter] = useState("all");

  const navigate = useNavigate();

  const getRaceId = (r) => r?.raceId ?? r?.id;

  const flagPath = (country) => {
    if (!country) return "";
    return `${process.env.PUBLIC_URL}/flags/${country
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ /g, "_")
      .toLowerCase()}.png`;
  };

  const isDDMMYYYY = (s) => typeof s === "string" && /^\d{2}-\d{2}-\d{4}$/.test(s);

  const tryParseDate = (s) => {
    if (!s) return null;

    try {
      if (isDDMMYYYY(s)) {
        const [dd, mm, yyyy] = s.split("-").map((x) => parseInt(x, 10));
        if (Number.isNaN(dd) || Number.isNaN(mm) || Number.isNaN(yyyy)) return null;
        return new Date(yyyy, mm - 1, dd);
      }

      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return null;

      const fixed = new Date(d);
      fixed.setDate(fixed.getDate() + 1);
      return fixed;
    } catch {
      return null;
    }
  };

  const formatDateDDMMYYYY = (s) => {
    if (!s) return "";
    if (isDDMMYYYY(s)) return s;

    const d = tryParseDate(s);
    if (!d) return String(s);

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const divisionOf = (race) => {
    const raw = race?.division;
    if (typeof raw === "string" && raw.trim()) return raw.trim();

    const g = Number(race?.groupCompetitionId);
    if (g === 0) return "World Tour";
    if (g === 1) return "Continental";
    if (g === 2) return "Pro Series";
    if (g === 3) return "Majeur";

    return "—";
  };

  const groupOf = (race) => {
    const d = divisionOf(race).toLowerCase().trim();
    if (d === "world tour") return 0;
    if (d === "majeur") return 1;
    if (d === "continental") return 2;
    if (d === "pro series") return 3;
    return -1;
  };

  const groupLabel = (g) => {
    switch (g) {
      case 0:
        return "World Tour";
      case 1:
        return "Majeur";
      case 2:
        return "Continental";
      case 3:
        return "Pro Series";
      default:
        return "Autre";
    }
  };

  const isGrandTour = (race) => {
    const name = (race?.name ?? "").toLowerCase();
    return (
      name.includes("tour de france") ||
      name.includes("giro d'italia") ||
      name.includes("giro di italia") ||
      name.includes("vuelta a espa")
    );
  };

  const byAnyId = useMemo(() => {
    const map = new Map();
    const add = (r) => {
      const id = getRaceId(r);
      if (id != null && !map.has(id)) map.set(id, r);
    };

    (races || []).forEach(add);
    (selectedRaces || []).forEach(add);
    (bonusRaces || []).forEach(add);

    return map;
  }, [races, selectedRaces, bonusRaces]);

  const available = useMemo(() => {
    const topTier = (races || []).filter((r) => {
      const g = groupOf(r);
      return g === 0 || g === 1;
    });

    const setIds = new Set([
      ...(selectedRaces || []).map(getRaceId).filter((x) => x != null),
      ...topTier.map(getRaceId).filter((x) => x != null),
    ]);

    return Array.from(setIds)
      .map((id) => byAnyId.get(id))
      .filter(Boolean);
  }, [races, selectedRaces, byAnyId]);

  const bonusIds = useMemo(() => {
    return new Set((bonusRaces || []).map(getRaceId).filter((x) => x != null));
  }, [bonusRaces]);

  const countByGroup = (g) => {
    let c = 0;
    for (const id of bonusIds) {
      const r = byAnyId.get(id);
      if (r && groupOf(r) === g) c++;
    }
    return c;
  };

  const grandToursCount = () => {
    let c = 0;
    for (const id of bonusIds) {
      const r = byAnyId.get(id);
      if (r && isGrandTour(r)) c++;
    }
    return c;
  };

  const wt = countByGroup(0);
  const maj = countByGroup(1);
  const cont = countByGroup(2);
  const pro = countByGroup(3);
  const gt = grandToursCount();
  const total = bonusIds.size;

  const isInvalidSelection = useMemo(() => {
    if (wt > 3 || maj > 3 || cont > 3 || pro > 3) return true;
    if (gt > 2) return true;
    if (total > 12) return true;
    if (total === 12 && (wt !== 3 || maj !== 3 || cont !== 3 || pro !== 3)) return true;
    return false;
  }, [wt, maj, cont, pro, gt, total]);

  const isBonusRaceSelected = (raceId) => bonusIds.has(raceId);

  const viewRaces = useMemo(() => {
    let list = [...available];

    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter((r) => {
        const name = (r?.name ?? "").toLowerCase();
        const type = (r?.courseType ?? "").toLowerCase();
        return name.includes(term) || type.includes(term);
      });
    }

    if (divisionFilter !== "all") {
      list = list.filter((r) => {
        const g = groupOf(r);
        if (divisionFilter === "wt") return g === 0;
        if (divisionFilter === "maj") return g === 1;
        if (divisionFilter === "cont") return g === 2;
        if (divisionFilter === "pro") return g === 3;
        return true;
      });
    }

    const compareByDateThenName = (a, b) => {
      const da = tryParseDate(a?.date_start);
      const db = tryParseDate(b?.date_start);
      if (da && db) {
        const c = da - db;
        if (c !== 0) return c;
      }
      return (a?.name ?? "").localeCompare(b?.name ?? "");
    };

    switch (sortBy) {
      case "name":
        list.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
        break;
      case "division":
        list.sort((a, b) => divisionOf(a).localeCompare(divisionOf(b)));
        break;
      case "type":
        list.sort((a, b) => (a?.courseType ?? "").localeCompare(b?.courseType ?? ""));
        break;
      case "date":
      default:
        list.sort(compareByDateThenName);
        break;
    }

    return list;
  }, [available, searchTerm, divisionFilter, sortBy]);

  const toggleBonus = (race) => {
    if (!race) return;
    setErrorMessage("");

    const id = getRaceId(race);
    if (id == null) return;

    if (bonusIds.has(id)) {
      setBonusRaces((bonusRaces || []).filter((r) => getRaceId(r) !== id));
      return;
    }

    if (total >= 12) {
      setErrorMessage(t("selectBonusRace.errors.maxTotal"));
      return;
    }

    const g = groupOf(race);
    if (g !== -1) {
      const currentInGroup = countByGroup(g);
      if (currentInGroup >= 3) {
        setErrorMessage(t("selectBonusRace.errors.maxPerDivision", { division: groupLabel(g) }));
        return;
      }
    }

    if (isGrandTour(race)) {
      if (gt >= 2) {
        setErrorMessage(t("selectBonusRace.errors.maxGrandTours"));
        return;
      }
    }

    setBonusRaces([...(bonusRaces || []), race]);
  };

  const validData = () => {
    setErrorMessage("");

    if (total !== 12 || wt !== 3 || maj !== 3 || cont !== 3 || pro !== 3 || gt > 2) {
      setErrorMessage(
        t("selectBonusRace.errors.invalidSummary", {
          wt,
          maj,
          cont,
          pro,
          gt,
          total,
        })
      );
      return;
    }

    saveData()
      .then(() => setConfirmValidDataPopup(true))
      .catch((error) => {
        console.error("Erreur lors de l'enregistrement :", error);
        setErrorMessage(t("selectBonusRace.errors.saveFailed"));
      });
  };

  const handleClosePopup = () => {
    navigate("/profil");
  };

  const sortedBonus = useMemo(() => {
    const list = [...(bonusRaces || [])];

    list.sort((a, b) => {
      const da = tryParseDate(a?.date_start);
      const db = tryParseDate(b?.date_start);
      if (da && db) {
        const c = da - db;
        if (c !== 0) return c;
      }
      return (a?.name ?? "").localeCompare(b?.name ?? "");
    });

    return list;
  }, [bonusRaces]);

  const firstLine = sortedBonus.filter((_, i) => i % 2 === 0);
  const secondLine = sortedBonus.filter((_, i) => i % 2 === 1);

  return (
    <main className="select-races-bonus">
      {confirmValidDataPopup && (
        <div className="select-races-bonus-popup">
          <div className="select-races-bonus-popup-content">
            <p>
              {t("selectBonusRace.popup.successLine1")}
              <br />
              {t("selectBonusRace.popup.successLine2")}
            </p>
            <button onClick={handleClosePopup}>{t("common.validate")}</button>
          </div>
        </div>
      )}

      <div className="select-races-bonus-shadow-container">
        <div className="select-races-bonus-title">
          <h1>{t("selectBonusRace.title")}</h1>
          <div className="select-races-bonus-shadow-mask-right"></div>
          <div className="select-races-bonus-shadow-mask-bottom"></div>
        </div>
      </div>

      <div className="select-races-bonus-header">
        <div className="select-races-bonus-rules">
          <div>
            <p>{t("selectBonusRace.subtitle")}</p>
            {errorMessage ? (
              <p className="select-races-bonus-error">{errorMessage}</p>
            ) : null}
          </div>
        </div>

        <div className="select-races-bonus-change-step">
          <button onClick={prevStep}>{t("common.previous")}</button>
          <button onClick={validData} disabled={isInvalidSelection}>
            {t("common.validate")}
          </button>
        </div>
      </div>

      <div className="select-races-bonus-pannel-view">
        <div className="select-races-bonus-choice">
          <div className="select-races-bonus-controls">
            <div className="select-races-bonus-search-control">
              <label>{t("selectBonusRace.controls.search")}</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("selectBonusRace.controls.searchPlaceholder")}
              />
            </div>

            <div className="select-races-bonus-filter-control">
              <label>{t("selectBonusRace.controls.division")}</label>
              <select value={divisionFilter} onChange={(e) => setDivisionFilter(e.target.value)}>
                <option value="all">{t("selectBonusRace.controls.divisions.all")}</option>
                <option value="wt">{t("selectBonusRace.controls.divisions.wt")}</option>
                <option value="maj">{t("selectBonusRace.controls.divisions.maj")}</option>
                <option value="cont">{t("selectBonusRace.controls.divisions.cont")}</option>
                <option value="pro">{t("selectBonusRace.controls.divisions.pro")}</option>
              </select>
            </div>

            <div className="select-races-bonus-sortby-control">
              <label>{t("selectBonusRace.controls.sort")}</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="date">{t("selectBonusRace.controls.sorts.date")}</option>
                <option value="name">{t("selectBonusRace.controls.sorts.name")}</option>
                <option value="type">{t("selectBonusRace.controls.sorts.type")}</option>
                <option value="division">{t("selectBonusRace.controls.sorts.division")}</option>
              </select>
            </div>
          </div>

          <div className="select-races-bonus-table-container">
            <table>
              <thead>
                <tr>
                  <th>{t("selectBonusRace.table.races")}</th>
                  <th>{t("selectBonusRace.table.date")}</th>
                  <th>{t("selectBonusRace.table.category")}</th>
                  <th>{t("selectBonusRace.table.division")}</th>
                  <th>{t("selectBonusRace.table.type")}</th>
                  <th>{t("selectBonusRace.table.profile")}</th>
                  <th>{t("selectBonusRace.table.steps")}</th>
                </tr>
              </thead>

              <tbody>
                {viewRaces.map((race) => {
                  const id = getRaceId(race);
                  const isSel = isBonusRaceSelected(id);

                  return (
                    <tr
                      key={id}
                      onClick={() => toggleBonus(race)}
                      className={isSel ? "select-races-bonus-table-selected-row" : ""}
                    >
                      <td>
                        <div className="select-races-bonus-table-name-container">
                          <img
                            className="select-races-table-flag-icon"
                            src={flagPath(race.country)}
                            alt={t("selectBonusRace.flagAlt", { country: race.country })}
                            width="20"
                            height="20"
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                          <span>{race.name}</span>
                        </div>
                      </td>

                      <td>{formatDateDDMMYYYY(race.date_start)}</td>
                      <td>{race.category}</td>
                      <td>{divisionOf(race)}</td>
                      <td>{race.courseType}</td>
                      <td>
                        {icons[race.profil] ? (
                          <img src={icons[race.profil]} alt={race.profil} width="25" height="25" />
                        ) : (
                          race.profil
                        )}
                      </td>
                      <td>{race.step}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="select-races-bonus-choice-view">
          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.counters.wt")}
            <span
              className={
                wt === 3
                  ? "select-races-bonus-selected-races-green"
                  : "select-races-bonus-selected-races-red"
              }
            >
              {wt}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.counters.maj")}
            <span
              className={
                maj === 3
                  ? "select-races-bonus-selected-races-green"
                  : "select-races-bonus-selected-races-red"
              }
            >
              {maj}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.counters.cont")}
            <span
              className={
                cont === 3
                  ? "select-races-bonus-selected-races-green"
                  : "select-races-bonus-selected-races-red"
              }
            >
              {cont}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.counters.pro")}
            <span
              className={
                pro === 3
                  ? "select-races-bonus-selected-races-green"
                  : "select-races-bonus-selected-races-red"
              }
            >
              {pro}
            </span>
            /3
          </p>

          <p className="select-races-bonus-selected-number">
            {t("selectBonusRace.counters.total")}
            <span
              className={
                total === 12
                  ? "select-races-bonus-selected-races-green"
                  : "select-races-bonus-selected-races-red"
              }
            >
              {total}
            </span>
            /12
            <span className="select-races-bonus-gt">
              ({t("selectBonusRace.counters.grandTours", { gt })})
            </span>
          </p>

          <div className="select-races-bonus-scroll-container">
            <div className="select-races-bonus-line-container">
              {firstLine.map((race) => {
                const id = getRaceId(race);
                return (
                  <div className="select-races-bonus-selected-cell" key={id}>
                    <div className="select-races-bonus-selected-info">
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                        alt={t("selectBonusRace.removeAlt")}
                        width="17"
                        height="17"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBonus(race);
                        }}
                      />

                      <div className="select-races-bonus-selected-name-container">
                        <img
                          className="select-races-bonus-selected-flag-icon"
                          src={flagPath(race.country)}
                          alt={t("selectBonusRace.flagAlt", { country: race.country })}
                          width="20"
                          height="20"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <h4 title={race.name}>{race.name}</h4>
                      </div>

                      <p>{race.category}</p>
                      <p>{divisionOf(race)}</p>
                      <p>
                        {icons[race.profil] ? (
                          <img src={icons[race.profil]} alt={race.profil} width="35" height="25" />
                        ) : (
                          race.profil
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="select-races-bonus-line-container">
              {secondLine.map((race) => {
                const id = getRaceId(race);
                return (
                  <div className="select-races-bonus-selected-cell" key={id}>
                    <div className="select-races-bonus-selected-info">
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/cancel.png`}
                        alt={t("selectBonusRace.removeAlt")}
                        width="17"
                        height="17"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleBonus(race);
                        }}
                      />

                      <div className="select-races-bonus-selected-name-container">
                        <img
                          className="select-races-bonus-selected-flag-icon"
                          src={flagPath(race.country)}
                          alt={t("selectBonusRace.flagAlt", { country: race.country })}
                          width="20"
                          height="20"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                        <h4 title={race.name}>{race.name}</h4>
                      </div>

                      <p>{race.category}</p>
                      <p>{divisionOf(race)}</p>
                      <p>
                        {icons[race.profil] ? (
                          <img src={icons[race.profil]} alt={race.profil} width="35" height="25" />
                        ) : (
                          race.profil
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SelectBonusRace;
